import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { useOrganizationsStore } from "@/stores/organizationsStore";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, ChevronRight } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

const CreateOrgFormSchema = z.object({
  name: z.string().min(1, "Organization name is required"),
});

const OrganizationSelector: React.FC = () => {
  const { user } = useAuthStore();
  const {
    organizations,
    fetchOrganizations,
    addOrganization,
    selectedOrganizationId,
    setSelectedOrganizationId,
  } = useOrganizationsStore();

  const [open, setOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrganizations(user?.organizations || []);
  }, [fetchOrganizations, user]);

  useEffect(() => {
    if (!selectedOrganizationId && organizations.length > 0) {
      setOpen(true);
    }
  }, [selectedOrganizationId, organizations]);

  const handleOrganizationChange = (orgId: string) => {
    setSelectedOrganizationId(orgId);
    setOpen(false);
    navigate("/calls");
  };

  const handleSheetOpenChange = (isOpen: boolean) => {
    if (!isOpen && !selectedOrganizationId && organizations.length > 0) {
      return;
    }
    setOpen(isOpen);
  };

  const form = useForm<z.infer<typeof CreateOrgFormSchema>>({
    resolver: zodResolver(CreateOrgFormSchema),
    defaultValues: {
      name: "",
    },
  });

  const onSubmit = async (data: z.infer<typeof CreateOrgFormSchema>) => {
    try {
      const newOrg = await api.createOrganization({ name: data.name });
      addOrganization(newOrg);
      toast({ title: "Organization created successfully" });
      form.reset();
      setIsDialogOpen(false);
      setSelectedOrganizationId(newOrg.id);
      navigate("/calls");
    } catch (error) {
      console.error("Failed to create organization:", error);
      toast({
        title: "Failed to create organization",
        description: String(error),
        variant: "destructive",
      });
    }
  };

  const selectedOrg = organizations.find(
    (org) => org.id === selectedOrganizationId
  );

  return (
    <div className="relative">
      <Button
        variant="outline"
        className="h-auto py-1 px-3 justify-between"
        onClick={() => setOpen(true)}
      >
        <div className="flex items-center gap-3">
          <span className="text-xs uppercase font-normal text-muted-foreground">
            ORG
          </span>
          <span className="font-bold text-sm">
            {selectedOrg ? selectedOrg.name : "Select Organization"}
          </span>
          <ChevronRight className="h-4 w-4 shrink-0 opacity-50" />
        </div>
      </Button>

      {open && (
        <div className="fixed inset-0 z-50 bg-background">
          <div className="container mx-auto flex flex-col h-full py-10">
            <div className="flex flex-col items-stretch gap-4 pb-6 border-b">
              <h2 className="text-2xl font-bold">Select an Organization</h2>
              <Button onClick={() => setIsDialogOpen(true)}>
                Create Organization
              </Button>
            </div>

            <div className="flex-1 overflow-auto py-6">
              {organizations.length === 0 ? (
                <Alert>
                  <AlertTitle>No Organizations Found</AlertTitle>
                  <AlertDescription>
                    You currently have no organizations. Please create one to
                    get started.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="grid gap-4 max-w-2xl">
                  {organizations.map((org) => (
                    <Card
                      key={org.id}
                      className={`cursor-pointer hover:bg-gray-100 transition-colors p-4 ${
                        selectedOrganizationId === org.id
                          ? "border-2 border-primary"
                          : ""
                      }`}
                      onClick={() => handleOrganizationChange(org.id)}
                    >
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold">{org.name}</h3>
                        {selectedOrganizationId === org.id && (
                          <Check className="h-5 w-5 text-primary" />
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end pt-6 border-t">
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={!selectedOrganizationId && organizations.length > 0}
              >
                {selectedOrganizationId ? "Close" : "Cancel"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Organization</DialogTitle>
            <DialogDescription>
              Fill in the form below to create a new organization.
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Organization Name"
                    {...form.register("name")}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
              <div className="flex justify-end space-x-2">
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button type="submit">Create Organization</Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrganizationSelector;
